import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

/** Extracts the sortable value for one column from a row. `null` sorts
 * last regardless of direction — used for entities with no business code. */
export type SortColumnAccessor<T> = (row: T) => string | number | null;

export interface SortState {
  key: string;
  direction: SortDirection;
}

/**
 * Smallest reusable client-side table-sort hook (Products/Operations/
 * Workers master-data tables —
 * NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md
 * §11-14). No table framework — current data volumes are small (tenant
 * master data, not paginated business records), so sorting is plain
 * client-side `Array.prototype.sort` over already-fetched rows, same
 * approach as the existing search ranking (`rankSearchMatches`).
 *
 * `null` values (e.g. a Product/Operation/Worker with no code) always sort
 * last, independent of direction — clicking a column to reverse direction
 * re-orders the CODED rows, it does not bury them under the uncoded ones.
 *
 * Code columns are compared as plain strings (`<`/`>`), never `Number()` —
 * fixed-width canonical codes make lexical order identical to numeric
 * order, so no parsing is needed or wanted.
 */
export function useSortableRows<T>(
  rows: T[],
  columns: Record<string, SortColumnAccessor<T>>,
  initial: SortState,
): {
  sorted: T[];
  sortKey: string;
  direction: SortDirection;
  requestSort: (key: string) => void;
} {
  const [sort, setSort] = useState<SortState>(initial);

  function requestSort(key: string) {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  }

  const sorted = useMemo(() => {
    const getValue = columns[sort.key];
    if (!getValue) return rows;
    const withValues = rows.map((row) => ({ row, value: getValue(row) }));
    withValues.sort((a, b) => compareSortValues(a.value, b.value, sort.direction));
    return withValues.map((entry) => entry.row);
  }, [rows, columns, sort.key, sort.direction]);

  return { sorted, sortKey: sort.key, direction: sort.direction, requestSort };
}

function compareSortValues(a: string | number | null, b: string | number | null, direction: SortDirection): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const raw = typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b));
  return direction === "asc" ? raw : -raw;
}
